<template>
  <div>
    <!-- Filtros -->
    <v-row v-if="isManager">
      <v-col cols="12" md="4">
        <SalesmanSearch @input="setSalesman($event)" />
      </v-col>
      <v-col cols="12" md="4">
        <CompanySelect
          @input="loadDashboardData"
          :item-value="(val) => val.id"
          v-model="searchParams.company_id"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedMonth"
          :items="monthOptions"
          label="Mês"
          dense
          @change="onMonthChange"
        />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedMonth"
          :items="monthOptions"
          label="Mês"
          dense
          @change="onMonthChange"
        />
      </v-col>
    </v-row>

    <!-- Cards Dashboard -->
    <v-row>
      <v-col cols="12" md="4">
        <app-card-dashboard
          title="Vendas no Mês"
          icon="mdi-cart-plus"
          iconColor="success"
        >
          <template v-slot:text>
            <h2>
              {{ formatDisplayValue($format.moneyBr(report.sale_total)) }}
              <v-icon
                v-if="showToggleIcon"
                @click="handleShowPassword"
                class="ml-2"
              >
                {{ displayValue ? "mdi-eye-off" : "mdi-eye" }}
              </v-icon>
            </h2>
          </template>
        </app-card-dashboard>
      </v-col>

      <v-col cols="12" md="4">
        <app-card-dashboard
          icon="mdi-account-group"
          iconColor="secondary"
          title="Atendimentos"
        >
          <template v-slot:text>
            <h2>
              {{
                formatDisplayValue(
                  $format.decimal(attendanceMonth.sales_count, 0),
                  "******",
                )
              }}
              <v-icon
                v-if="showToggleIcon"
                @click="handleShowPassword"
                class="ml-2"
              >
                {{ displayValue ? "mdi-eye-off" : "mdi-eye" }}
              </v-icon>
            </h2>
          </template>
        </app-card-dashboard>
      </v-col>

      <v-col cols="12" md="4">
        <app-card-dashboard
          icon="mdi-ticket"
          iconColor="primary"
          title="Ticket Médio"
        >
          <template v-slot:text>
            <h2>
              {{ formatDisplayValue($format.moneyBr(averageTicket, 0)) }}
              <v-icon
                v-if="showToggleIcon"
                @click="handleShowPassword"
                class="ml-2"
              >
                {{ displayValue ? "mdi-eye-off" : "mdi-eye" }}
              </v-icon>
            </h2>
          </template>
        </app-card-dashboard>
      </v-col>
    </v-row>

    <!-- Tabelas e Gráficos -->
    <v-row>
      <v-col cols="12" md="5">
        <v-card outlined v-if="shouldShowData">
          <v-card-title>Entregas Pendentes/Parcial</v-card-title>
          <v-card-subtitle>
            {{ sales.total }} registro(s) encontrado(s)
          </v-card-subtitle>

          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="sales.data"
              :items-per-page="-1"
              hide-default-footer
              disable-sort
              dense
              @click:row="handleSaleView($event)"
            >
              <template v-slot:[`item.customer`]="{ item }">
                <span>{{ getCustomerName(item) }}</span>
                <div class="pb-1">
                  <v-chip color="primary" x-small>
                    {{ item.operation.description }} #{{ item.number }}
                  </v-chip>
                  <small class="pl-2">
                    {{ $format.dateTimeBr(item.created_at) }}
                  </small>
                </div>
              </template>

              <template v-slot:[`item.delivery_status`]="{ item }">
                <v-menu bottom left class="mr-1">
                  <template v-slot:activator="{ on }">
                    <SaleDeliveryStatus
                      v-on="on"
                      :deliveryStatus="item.delivery_status"
                    />
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-if="canStoreDelivery(item)"
                      @click="scheduleDelivery(item.id)"
                      :disabled="item.delivery_status == 'delivered'"
                    >
                      <v-list-item-icon class="mr-2">
                        <app-icon>local_shipping</app-icon>
                      </v-list-item-icon>
                      <v-list-item-content
                        >Agendar Entregar</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item
                      v-if="canViewDeliveries(item)"
                      @click="handleViewDeliveries(item.number)"
                    >
                      <v-list-item-icon class="mr-2">
                        <app-icon>travel_explore</app-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Ver entregas</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-data-table>
            <app-pagination @click="getSales($event)" :data="sales" />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title>
            Vendas
            <span class="caption ml-3 text--secondary">
              (Dos Últimos 6 Meses)
            </span>
          </v-card-title>
          <v-card-text>
            <app-charts :options="chartOptions" :series="chartSeries" />
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-6">
          <v-card-title>
            Contas a Receber na Entrega
            <v-spacer />
            <v-btn
              @click="$router.push('/dashboard/contas-a-receber-na-entrega')"
              small
              text
              color="primary"
            >
              <v-icon small>mdi-open-in-new</v-icon>
              Ver mais
            </v-btn>
          </v-card-title>
        </v-card>

        <v-card outlined class="mt-6" v-if="shouldShowData">
          <v-card-title>
            Vendas por Categoria
            <span class="caption ml-3 text--secondary">
              (Do Mês Selecionado)
            </span>
          </v-card-title>
          <v-data-table
            :headers="salesByCategoryHeaders"
            :items="salesByCategory"
            hide-default-footer
            :items-per-page="-1"
            disable-sort
          >
            <template v-slot:item.quantity="{ item }">
              {{ $format.decimal(item.quantity) }}
            </template>

            <template v-slot:item.percentage="{ item }">
              <div class="d-flex align-center">
                <v-progress-linear
                  :value="parseFloat(calculatePercentage(item))"
                  height="8"
                  rounded
                  color="primary"
                  class="mr-2"
                  style="min-width: 60px;"
                ></v-progress-linear>
                <span class="caption">{{ calculatePercentage(item) }}%</span>
              </div>
            </template>

            <template v-slot:item.total="{ item }">
              R$ {{ $format.decimal(item.total) }}
            </template>
          </v-data-table>
        </v-card>

        <v-card outlined class="mt-6" v-if="shouldShowData">
          <v-card-title>
            Vendas por Cliente
            <span class="caption ml-3 text--secondary">
              (Dos Últimos 7 dias)
            </span>
          </v-card-title>
          <v-card-subtitle class="caption text--secondary">
            Clique no número para abrir o WhatsApp
          </v-card-subtitle>
          <v-data-table
            :headers="salesByCustomerHeaders"
            :items="salesByCustomer.data"
            hide-default-footer
            :items-per-page="-1"
            disable-sort
          >
            <template v-slot:item.customer_name="{ item }">
              <div
                :class="
                  `d-flex align-center py-1 ${
                    $vuetify.breakpoint.xsOnly ? 'justify-end' : 'justify-start'
                  } `
                "
              >
                <strong class="primary--secondary subtitle-2">
                  {{ item.customer_name }}
                </strong>
                <v-chip
                  v-if="item.is_special"
                  color="success"
                  x-small
                  class="ml-2 white--text"
                >
                  Especial
                </v-chip>
                <v-chip
                  v-if="item.is_blocked"
                  color="error"
                  x-small
                  class="ml-1 white--text"
                >
                  Bloqueado
                </v-chip>
              </div>
              <div
                :class="
                  `caption grey--text text--darken-1 mt-1 ${
                    $vuetify.breakpoint.xsOnly ? 'text-end' : 'text-start'
                  } `
                "
              >
                Última compra:
                {{ $format.dateBr(item.last_purchase_date) }}
                às
                {{ $format.timeBr(item.last_purchase_date) }}
              </div>
            </template>

            <template v-slot:item.total="{ item }">
              R$ {{ $format.decimal(item.total) }}
            </template>

            <template v-slot:item.phone_wa="{ item }">
              <v-btn
                v-if="item.phone_wa"
                color="success"
                text
                rounded
                :icon="$vuetify.breakpoint.xsOnly"
                :class="
                  `white--text ${$vuetify.breakpoint.xsOnly ? '' : 'ml-2'}`
                "
                :href="`https://wa.me/${item.phone_wa.replace(/\D/g, '')}`"
                target="_blank"
                style="letter-spacing: -0.5px; font-size: 0.85rem;"
              >
                <v-icon
                  :small="$vuetify.breakpoint.xsOnly"
                  :x-small="!$vuetify.breakpoint.xsOnly"
                  >mdi-whatsapp</v-icon
                >
                <span v-if="!$vuetify.breakpoint.xsOnly" class="ml-2">
                  {{ $format.phone(item.phone_wa.replace(/\D/g, "")) }}
                </span>
              </v-btn>
            </template>
          </v-data-table>
          <app-pagination
            @click="getSalesByCustomer($event)"
            :data="salesByCustomer"
          />
        </v-card>
      </v-col>
    </v-row>

    <SaleView ref="SaleView" @store="loadDashboardData" />

    <!-- Dialog de Senha -->
    <v-dialog
      v-model="dialog"
      persistent
      max-width="600px"
      @keydown.enter="handleConfirm"
    >
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>
            <h5>Digite sua senha de usuário</h5>
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-text-field
            label="Senha"
            type="password"
            autocomplete="off"
            autofocus
            v-model="searchParams.password"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="dialog = false">Fechar</v-btn>
          <v-btn color="primary" @click="handleConfirm">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SaleDeliveryStatus from "@/components/sales/sections/SaleDeliveryStatus.vue";
import SaleView from "@/components/sales/sections/SaleView.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import CompanySelect from "@/components/company/ui/CompanySelect";
import { format, startOfMonth } from "date-fns";

export default {
  components: {
    SaleDeliveryStatus,
    SaleView,
    SalesmanSearch,
    CompanySelect,
  },

  data() {
    return {
      report: {
        sales_report: [],
        attendance_report: [],
        devolution_report: [],
      },

      sales: {},
      salesByCategory: [],
      salesByCustomer: [],
      billReceivableCashOnDelivery: [],
      billReceivableCashOnDeliveryTotals: [],

      searchParams: {
        delivery_status: ["not_delivered", "partial"],
        status: ["sale"],
        delivery_type: "delivery",
        order_by: "asc",
        password: "",
      },

      groupedByCategorySearchParams: {
        due_date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        due_date_end: format(new Date(), "yyyy-MM-dd"),
        status: ["sale"],
        salesman_id: null,
        company_id: null,
      },

      bill_receivable_headers: [
        { text: "Venda", value: "sale.number", align: "start", width: "3%" },
        {
          text: "Cliente",
          value: "customer.name",
          align: "start",
          width: "30%",
        },
        { text: "Vencimento", value: "due_date", align: "center" },
        { text: "Valor", value: "value", align: "end", width: "15%" },
        { text: "Valor Pago", value: "paid_value", align: "end", width: "15%" },
        { text: "Saldo", value: "remainder_value", align: "end", width: "15%" },
        { text: "Status", value: "status", align: "center" },
      ],

      selectedMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear(),
      displayValue: false,
      dialog: false,
      isLoading: false,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.userData.user;
    },

    isManager() {
      return this.user.position === "gerente" || this.user.super_admin === 1;
    },

    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB10|IEMobile|Opera Mini|Mobile|Tablet|Kindle|Silk|Windows Phone|webOS|PlayBook|MeeGo/i.test(
        navigator.userAgent,
      );
    },

    showToggleIcon() {
      return !this.isManager && !this.isMobile;
    },

    shouldShowData() {
      return this.displayValue || this.isManager || this.isMobile;
    },

    month() {
      return `${this.currentYear}-${String(this.selectedMonth).padStart(
        2,
        "0",
      )}`;
    },

    salesMonth() {
      return (
        this.report.sales_report.find(
          (item) => item.date_month === this.month,
        ) || {}
      );
    },

    devolutionMonth() {
      return (
        this.report.devolution_report.find(
          (item) => item.date_month === this.month,
        ) || {}
      );
    },

    attendanceMonth() {
      return (
        this.report.attendance_report.find(
          (item) => item.date_month === this.month,
        ) || {}
      );
    },

    averageTicket() {
      const salesCount = this.attendanceMonth.sales_count || 0;
      return salesCount > 0 ? Number(this.report.sale_total / salesCount) : 0;
    },

    chartOptions() {
      if (!this.shouldShowData) return {};

      return {
        xaxis: {
          categories: this.report.sales_report.map((item) =>
            this.$format.dateBr(item.date_month, "MMM/y"),
          ),
        },
        yaxis: {
          labels: {
            formatter: (value) => this.$format.moneyBr(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => this.$format.moneyBr(value),
          },
          enabled: true,
        },
      };
    },

    chartSeries() {
      if (!this.shouldShowData) return [];

      return [
        {
          name: "Vendas",
          data: this.report.sales_report.map((item) => item.net_total_sum),
        },
        {
          name: "Devoluções",
          data: this.report.devolution_report.map((item) => item.net_total_sum),
        },
      ];
    },

    headers() {
      return [
        { text: "Cliente", value: "customer" },
        {
          text: "Entrega",
          value: "delivery_status",
          align: "center",
          width: "10%",
        },
      ];
    },

    salesByCategoryHeaders() {
      return [
        { text: "Categoria", value: "category_name", align: "start" },
        { text: "Quantidade", value: "quantity", align: "start" },
        { text: "% Valor total", value: "percentage", align: "center" },
        { text: "Total", value: "total", align: "end" },
      ];
    },

    salesByCustomerHeaders() {
      return [
        { text: "Cliente", value: "customer_name", align: "start" },
        { text: "WhatsApp", value: "phone_wa", align: "center" },
        { text: "Total", value: "total", align: "end" },
      ];
    },

    monthOptions() {
      return [
        { text: "Janeiro", value: 1 },
        { text: "Fevereiro", value: 2 },
        { text: "Março", value: 3 },
        { text: "Abril", value: 4 },
        { text: "Maio", value: 5 },
        { text: "Junho", value: 6 },
        { text: "Julho", value: 7 },
        { text: "Agosto", value: 8 },
        { text: "Setembro", value: 9 },
        { text: "Outubro", value: 10 },
        { text: "Novembro", value: 11 },
        { text: "Dezembro", value: 12 },
      ];
    },
  },

  watch: {
    selectedMonth() {
      this.updateDateRange();
      this.loadDashboardData();
    },

    displayValue() {
      this.loadDashboardData();
    },
  },

  created() {
    this.initializeDashboard();
  },

  mounted() {
    if (this.isManager) {
      this.displayValue = true;
    }
  },

  methods: {
    initializeDashboard() {
      if (!this.isManager) {
        this.setSalesman(this.user);
      } else {
        this.loadDashboardData();
      }
    },

    formatDisplayValue(value, hiddenValue = "R$ **************") {
      return this.shouldShowData ? value : hiddenValue;
    },

    getCustomerName(item) {
      return item.customer ? item.customer.name : "Consumidor Final";
    },

    calculatePercentage(item) {
      if (!item.grand_total || parseFloat(item.grand_total) === 0) {
        return "0";
      }
      const percentage =
        (parseFloat(item.total) / parseFloat(item.grand_total)) * 100;
      return this.$format.decimal(percentage);
    },

    canStoreDelivery(sale) {
      return (
        sale.status === "sale" &&
        sale.delivery_type !== "no_delivery" &&
        this.$acl.can("deliveryCreateEdit")
      );
    },

    canViewDeliveries(item) {
      return (
        item.delivery_type === "delivery" && this.$acl.can("deliveriesIndex")
      );
    },

    async loadDashboardData() {
      if (!this.shouldShowData) return;

      const requestParams = {
        ...this.searchParams,
        month: this.selectedMonth,
        year: this.currentYear,
      };

      try {
        const response = await this.$http.index(
          "dashboard/sales",
          requestParams,
        );
        this.report = response;
        this.getSales();
        this.getSalesByCategory();
        this.getSalesByCustomer();
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard:", error);
      }
    },

    async getSales(page = 1) {
      this.searchParams.page = page;
      this.$loading.start();

      try {
        const response = await this.$http.index("sale/sale", this.searchParams);
        this.sales = response.sales;
      } catch (error) {
        console.error("Erro ao carregar vendas:", error);
      } finally {
        this.$loading.finish();
      }
    },

    async getSalesByCustomer(page = 1) {
      this.searchParams.page = page;
      this.$loading.start();

      const salesmanId = this.isManager
        ? this.groupedByCategorySearchParams.salesman_id
        : this.user.id;

      try {
        const response = await this.$http.index(
          "sale/sale-by-customer-report",
          { salesman_id: salesmanId, page },
        );
        this.salesByCustomer = response;
      } catch (error) {
        console.error("Erro ao carregar vendas por cliente:", error);
      } finally {
        this.$loading.finish();
      }
    },

    async getSalesByCategory() {
      if (!this.shouldShowData) return {};

      this.isLoading = true;

      const salesmanId = this.isManager
        ? this.groupedByCategorySearchParams.salesman_id
        : this.user.id;

      const payload = {
        ...this.groupedByCategorySearchParams,
        salesman_id: salesmanId,
      };

      try {
        const response = await this.$http.index(
          "sale/sale-by-category-report",
          payload,
        );
        this.salesByCategory = response.data;
      } catch (error) {
        console.error("Erro ao carregar vendas por categoria:", error);
      } finally {
        this.isLoading = false;
      }
    },

    setSalesman(salesman) {
      this.searchParams.salesman_id = salesman.id;
      this.groupedByCategorySearchParams.salesman_id = salesman.id;
      this.loadDashboardData();
    },

    handleSaleView(sale) {
      this.$refs.SaleView.open(sale.id);
    },

    handleShowPassword() {
      if (this.displayValue) {
        this.displayValue = false;
        return;
      }
      this.dialog = true;
    },

    async handleConfirm() {
      this.$loading.start();

      try {
        const response = await this.$http.store(
          "dashboard/sales",
          this.searchParams,
        );
        this.displayValue = response.status;
        this.dialog = false;
      } catch (error) {
        console.error("Erro na validação da senha:", error);
      } finally {
        this.searchParams.password = "";
        this.$loading.finish();
      }
    },

    scheduleDelivery(saleId) {
      window.open(`/entrega/novo/${saleId}`);
    },

    handleViewDeliveries(saleNumber) {
      window.open(
        `/vendas/visualizar-entrega/?sale_number=${saleNumber}`,
        "_blank",
      );
    },

    onMonthChange() {
      this.updateDateRange();
      this.loadDashboardData();
    },

    updateDateRange() {
      const startDate = new Date(this.currentYear, this.selectedMonth - 1, 1);
      const endDate = new Date(this.currentYear, this.selectedMonth, 0);

      this.groupedByCategorySearchParams.due_date_start = format(
        startDate,
        "yyyy-MM-dd",
      );
      this.groupedByCategorySearchParams.due_date_end = format(
        endDate,
        "yyyy-MM-dd",
      );
    },
  },
};
</script>
